/**
 * Status badges shared by every page. One mapping per enum so status colors stay consistent.
 * Tones come from Arc's Badge: neutral | success | info | warning | danger.
 */
import { Badge, type BadgeTone } from '@/components/arc/badge/badge';
import { labelize } from '@/lib/format';
import type {
  AnalysisStatus,
  CampaignStatus,
  CreativeFormat,
  ExportStatus,
  JobStatus,
  Platform,
  PublisherStatus,
  SegmentStatus,
  SourceStatus,
  SourceType,
} from '@/api/types';

const ANALYSIS: Record<AnalysisStatus, BadgeTone> = {
  queued: 'neutral',
  analyzing: 'info',
  done: 'success',
  failed: 'danger',
  skipped: 'warning',
};
const SOURCE: Record<SourceStatus, BadgeTone> = {
  pending: 'neutral',
  discovering: 'info',
  ready: 'success',
  error: 'danger',
};
const SEGMENT: Record<SegmentStatus, BadgeTone> = {
  draft: 'neutral',
  active: 'success',
  archived: 'neutral',
};
const EXPORT: Record<ExportStatus, BadgeTone> = {
  queued: 'neutral',
  running: 'info',
  done: 'success',
  failed: 'danger',
};
const PUBLISHER: Record<PublisherStatus, BadgeTone> = {
  active: 'success',
  paused: 'warning',
  disabled: 'neutral',
};
const CAMPAIGN: Record<CampaignStatus, BadgeTone> = {
  draft: 'neutral',
  active: 'success',
  paused: 'warning',
  ended: 'neutral',
};
const JOB: Record<JobStatus, BadgeTone> = {
  queued: 'neutral',
  running: 'info',
  done: 'success',
  failed: 'danger',
};

function tone<T extends string>(map: Record<T, BadgeTone>, v: T): BadgeTone {
  return map[v] ?? 'neutral';
}

export function AnalysisStatusBadge({ status }: { status: AnalysisStatus }) {
  return (
    <Badge tone={tone(ANALYSIS, status)} size="sm">
      {labelize(status)}
    </Badge>
  );
}
export function SourceStatusBadge({ status }: { status: SourceStatus }) {
  return (
    <Badge tone={tone(SOURCE, status)} size="sm">
      {labelize(status)}
    </Badge>
  );
}
export function SegmentStatusBadge({ status }: { status: SegmentStatus }) {
  return (
    <Badge tone={tone(SEGMENT, status)} size="sm">
      {labelize(status)}
    </Badge>
  );
}
export function ExportStatusBadge({ status }: { status: ExportStatus }) {
  return (
    <Badge tone={tone(EXPORT, status)} size="sm">
      {labelize(status)}
    </Badge>
  );
}
export function PublisherStatusBadge({ status }: { status: PublisherStatus }) {
  return (
    <Badge tone={tone(PUBLISHER, status)} size="sm">
      {labelize(status)}
    </Badge>
  );
}
export function CampaignStatusBadge({ status }: { status: CampaignStatus }) {
  return (
    <Badge tone={tone(CAMPAIGN, status)} size="sm">
      {labelize(status)}
    </Badge>
  );
}
export function JobStatusBadge({ status }: { status: JobStatus }) {
  return (
    <Badge tone={tone(JOB, status)} size="sm">
      {labelize(status)}
    </Badge>
  );
}

/** Neutral label badges for enums that are categories, not states. */
export function SourceTypeBadge({ type }: { type: SourceType }) {
  return <Badge size="sm">{labelize(type)}</Badge>;
}
export function PlatformBadge({ platform }: { platform: Platform }) {
  return <Badge size="sm">{platform === 'ctv' ? 'CTV' : labelize(platform)}</Badge>;
}
export function CreativeFormatBadge({ format }: { format: CreativeFormat }) {
  return <Badge size="sm">{format === 'simid' ? 'SIMID' : labelize(format)}</Badge>;
}
